import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { MARKETPLACE_ERROR_HANDLERS, ProductPersonalizationAddition } from '@platform-frontend/marketplace';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { handleErrors, InvalidStateError, ShowErrorNotification } from '@platform-frontend/util';
import { Observable, of, throwError } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { FetchFlowData, FlowCanceled, PublishFetchedFlowData, SetFlow } from '../../flow';
import { MSG_SET_FIRST_PERSON_DEFAULT } from '../../notification-messages';
import { ProductState } from '../../product';
import { PersonalDetails } from './model/personal-details';
import { PersonalDetailsWithFullName } from './model/personal-details-with-full-name';
import { PersonalizationPerson } from './model/personalization-person';
import { ShoppingCartPersonalizationStateModel } from './model/shopping-cart-personalization-state-model';
import { SetAdditionalPersons, SetFirstPerson } from './shopping-cart-personalization.actions';

const DEFAULT_STATE: ShoppingCartPersonalizationStateModel = {
  loadedUserPersonalDetails: null,
  userPersonalDetails: null,
  firstPerson: null,
  additionalPersons: [],
  metadataId: null
};

@State<ShoppingCartPersonalizationStateModel>({
  name: 'shoppingCartPersonalization',
  defaults: DEFAULT_STATE
})
@Injectable()
export class ShoppingCartPersonalizationState {
  constructor(private http: HttpClient, private store: Store) {}

  personalizationAddition$: Observable<ProductPersonalizationAddition | null> = this.store.select(ProductState.personalizationAddition);

  @Selector()
  static userPersonalDetails(state: ShoppingCartPersonalizationStateModel): PersonalDetails<string | null> | null {
    return state.userPersonalDetails;
  }

  @Selector()
  static firstPerson(state: ShoppingCartPersonalizationStateModel): PersonalizationPerson<string | null> | null {
    return state.firstPerson;
  }

  @Selector()
  static additionalPersons(state: ShoppingCartPersonalizationStateModel): PersonalDetailsWithFullName[] {
    return state.additionalPersons;
  }

  @Selector([ShoppingCartPersonalizationState.firstPerson, ShoppingCartPersonalizationState.additionalPersons])
  static allPersons(
    state: ShoppingCartPersonalizationStateModel,
    firstPerson: PersonalizationPerson<string | null> | null,
    additionalPersons: PersonalDetailsWithFullName[]
  ): PersonalizationPerson<string | null>[] {
    const persons: PersonalizationPerson<string | null>[] = [];
    if (firstPerson) {
      persons.push(firstPerson);
    }
    additionalPersons.forEach(person => {
      persons.push({ ...person, isUser: false });
    });
    return persons;
  }

  @Selector([ShoppingCartPersonalizationState.allPersons, ProductState.maxNumberOfPersons])
  static canPersonsBeAdded(
    state: ShoppingCartPersonalizationStateModel,
    allPersons: PersonalizationPerson<string | null>[],
    maxNumberOfPersons: number
  ): boolean {
    return allPersons.length < maxNumberOfPersons;
  }

  @Selector([ShoppingCartPersonalizationState.allPersons])
  static arePersonsSet(state: ShoppingCartPersonalizationStateModel, allPersons: PersonalizationPerson<string | null>[]): boolean {
    return allPersons.length > 0;
  }

  @Action(FetchFlowData)
  loadPersonalDetails(ctx: StateContext<ShoppingCartPersonalizationStateModel>, { flow }: FetchFlowData): Observable<unknown> {
    if (flow.type !== 'COMMERCIAL_CHECKOUT') {
      return of(null);
    }

    return this.personalizationAddition$.pipe(
      take(1),
      filter(addition => addition !== null),
      switchMap(() =>
        this.http.get<PersonalDetails<string | null, string>>(PLATFORM_ENVIRONMENT.apiUrl + '/account/personal-details').pipe(
          map(loadedPersonalDetails => this.mapUserPersonalDetailsFromBackend(loadedPersonalDetails)),
          tap(loadedUserPersonalDetails => ctx.patchState({ loadedUserPersonalDetails, metadataId: flow.startParams.metadataId }))
        )
      )
    );
  }

  private mapUserPersonalDetailsFromBackend(userPersonalDetails: PersonalDetails<string | null, string>): PersonalDetails<string | null> | null {
    if (!userPersonalDetails || !this.areAnyPersonalDetailsSet(userPersonalDetails)) return null;
    return {
      ...userPersonalDetails,
      birthDate: userPersonalDetails.birthDate ? new Date(userPersonalDetails.birthDate) : null
    };
  }

  @Action(PublishFetchedFlowData)
  publishPersonalDetails(ctx: StateContext<ShoppingCartPersonalizationStateModel>): void {
    const { loadedUserPersonalDetails, firstPerson } = ctx.getState();
    const fullName: string | null =
      loadedUserPersonalDetails && (loadedUserPersonalDetails.firstName || loadedUserPersonalDetails.lastName)
        ? (loadedUserPersonalDetails.firstName ?? '') +
          (loadedUserPersonalDetails.firstName && loadedUserPersonalDetails.lastName ? ' ' : '') +
          (loadedUserPersonalDetails.lastName ?? '')
        : null;
    const newFirstPerson: PersonalizationPerson<string | null> | null =
      firstPerson && !firstPerson.isUser
        ? firstPerson
        : loadedUserPersonalDetails && this.areAnyPersonalDetailsSet(loadedUserPersonalDetails)
        ? { ...loadedUserPersonalDetails, fullName, isUser: true }
        : null;
    ctx.patchState({
      userPersonalDetails: loadedUserPersonalDetails,
      loadedUserPersonalDetails: DEFAULT_STATE.loadedUserPersonalDetails,
      firstPerson: newFirstPerson
    });
  }

  @Action(SetFlow)
  setFlow(ctx: StateContext<ShoppingCartPersonalizationStateModel>, { flow }: SetFlow): void {
    if (flow.startParams.metadataId !== ctx.getState().metadataId) ctx.patchState(DEFAULT_STATE);
  }

  @Action(FlowCanceled)
  flowCanceled(ctx: StateContext<ShoppingCartPersonalizationStateModel>): void {
    ctx.patchState(DEFAULT_STATE);
  }

  @Action(SetFirstPerson)
  setFirstPerson(ctx: StateContext<ShoppingCartPersonalizationStateModel>, { firstPerson }: SetFirstPerson): Observable<unknown> {
    return this.personalizationAddition$.pipe(
      take(1),
      switchMap(addition =>
        addition === null
          ? throwError(() => new InvalidStateError('Persons are not required to be set!'))
          : firstPerson.isUser
          ? this.persistUserData(ctx, firstPerson, addition)
          : of(null)
      ),
      tap(() => ctx.patchState({ firstPerson: this.areAnyPersonalDetailsSet(firstPerson) ? firstPerson : null })),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, { DEFAULT: new ShowErrorNotification(MSG_SET_FIRST_PERSON_DEFAULT) })
    );
  }

  private persistUserData(
    ctx: StateContext<ShoppingCartPersonalizationStateModel>,
    firstPerson: PersonalizationPerson<string | null>,
    addition: ProductPersonalizationAddition
  ): Observable<unknown> {
    return this.http
      .put<PersonalDetails<string | null, string>>(PLATFORM_ENVIRONMENT.apiUrl + '/account/personal-details', {
        firstName: firstPerson.firstName,
        lastName: firstPerson.lastName,
        birthDate: firstPerson.birthDate?.toISOString().slice(0, 10) ?? null,
        updateBirthDate: addition.birthDateRequirement !== 'NONE',
        address: firstPerson.address
      })
      .pipe(tap(() => ctx.patchState({ userPersonalDetails: firstPerson })));
  }

  private areAnyPersonalDetailsSet(personalDetails: PersonalDetails<string | null, Date | string>): boolean {
    return personalDetails.firstName !== null || personalDetails.lastName !== null || personalDetails.birthDate !== null;
  }

  @Action(SetAdditionalPersons)
  setAdditionalPersons(ctx: StateContext<ShoppingCartPersonalizationStateModel>, { additionalPersons }: SetAdditionalPersons): Observable<unknown> {
    return this.personalizationAddition$.pipe(
      take(1),
      switchMap(addition =>
        addition === null
          ? throwError(() => new InvalidStateError('Persons are not required to be set!'))
          : addition.maxNumberOfPersons < additionalPersons.length + 1
          ? throwError(() => new InvalidStateError('Too many persons are to be set!'))
          : of(null)
      ),
      tap(() => ctx.patchState({ additionalPersons })),
      handleErrors(MARKETPLACE_ERROR_HANDLERS)
    );
  }
}
